import { useCallback, useEffect, useLayoutEffect } from 'react'
import type { ShapeBounds } from '@/lib/shapes/types/shape-bounds'
import type { ShapeManagerStateApi } from './use-shape-manager-state'

/**
 * Measured per-shape bounds: keeps `shapeBoundsRef` in step with state, prunes entries for removed
 * or resized shapes, and exposes the measurement callback and the imperative read.
 */
export function useShapeBounds(ctx: ShapeManagerStateApi) {
  const { setShapeBounds, shapeBounds, shapeBoundsRef, shapePlacementDimsRef, shapes } = ctx

  useEffect(() => {
    shapeBoundsRef.current = shapeBounds
  }, [shapeBounds, shapeBoundsRef])

  useLayoutEffect(() => {
    const dims = shapePlacementDimsRef.current
    const idsNow = new Set(shapes.map((s) => s.id))
    const boundsMap = shapeBoundsRef.current
    let shouldPruneBounds = false
    const removedShapeIds = new Set<string>()
    const resizedShapeIds = new Set<string>()

    for (const id of [...Object.keys(dims)]) {
      if (idsNow.has(id)) continue

      delete dims[id]
      if (boundsMap[id]) {
        shouldPruneBounds = true
        removedShapeIds.add(id)
      }
    }

    for (const s of shapes) {
      const w = s.width ?? 0
      const h = s.height ?? 0
      const prior = dims[s.id]

      if (prior !== undefined && (prior.w !== w || prior.h !== h) && boundsMap[s.id]) {
        shouldPruneBounds = true
        resizedShapeIds.add(s.id)
      }

      dims[s.id] = { w, h }
    }

    if (!shouldPruneBounds) return

    setShapeBounds((prev) => {
      let next: Record<string, ShapeBounds> | null = null
      const touch = () => {
        if (!next) next = { ...prev }
      }

      for (const id of removedShapeIds) {
        if (!prev[id]) continue
        touch()
        delete next![id]
      }

      for (const id of resizedShapeIds) {
        if (!prev[id]) continue
        touch()
        delete next![id]
      }

      const resolved = next ?? prev

      // `getShapeBounds` reads this ref imperatively (object panel, multi-select). Keep it in
      // lockstep with pruned state here so callers in the same commit do not mix new placement
      // W/H with pre-resize path bounds (wrong rotated AABB / X,Y until a later effect tick).
      shapeBoundsRef.current = resolved
      return resolved
    })
  }, [shapes, setShapeBounds, shapeBoundsRef, shapePlacementDimsRef])

  const handleBoundsChange = useCallback(
    (shapeId: string, bounds: ShapeBounds) => {
      setShapeBounds((prev) => {
        const current = prev[shapeId]

        if (
          current &&
          current.x === bounds.x &&
          current.y === bounds.y &&
          current.width === bounds.width &&
          current.height === bounds.height
        ) {
          return prev
        }

        return { ...prev, [shapeId]: bounds }
      })
    },
    [setShapeBounds]
  )

  const getShapeBounds = useCallback(
    (shapeId: string) => {
      return shapeBoundsRef.current[shapeId]
    },
    [shapeBoundsRef]
  )

  return { handleBoundsChange, getShapeBounds }
}

export type ShapeBoundsApi = ReturnType<typeof useShapeBounds>
